import { extractUkPostcode, isUkPostcodeOnly } from "./ukPostcode";
import { PLACES_STATUS_OK } from "./googleMapsReady";

export type AddressSuggestion = {
  placeId: string;
  description: string;
  mainText?: string;
  secondaryText?: string;
  location?: { lat: number; lng: number };
};

const geocodeCache = new Map<string, google.maps.GeocoderResult>();

function geocodePostcode(
  geocoder: google.maps.Geocoder,
  postcode: string,
): Promise<google.maps.GeocoderResult | null> {
  const cached = geocodeCache.get(postcode);
  if (cached) return Promise.resolve(cached);

  return new Promise((resolve) => {
    geocoder.geocode(
      {
        address: postcode,
        componentRestrictions: { country: "GB" },
      },
      (results, status) => {
        if (status !== "OK" || !results?.[0]) {
          resolve(null);
          return;
        }
        geocodeCache.set(postcode, results[0]);
        resolve(results[0]);
      },
    );
  });
}

function autocompletePredictions(
  service: google.maps.places.AutocompleteService,
  request: google.maps.places.AutocompletionRequest,
): Promise<google.maps.places.AutocompletePrediction[]> {
  return new Promise((resolve) => {
    service.getPlacePredictions(request, (predictions, status) => {
      if (String(status) === PLACES_STATUS_OK && predictions) {
        resolve(predictions);
      } else {
        resolve([]);
      }
    });
  });
}

function textSearchAddresses(
  service: google.maps.places.PlacesService,
  query: string,
  bounds?: google.maps.LatLngBounds,
): Promise<google.maps.places.PlaceResult[]> {
  return new Promise((resolve) => {
    const request: google.maps.places.TextSearchRequest = {
      query,
      ...(bounds ? { bounds } : {}),
    };
    service.textSearch(request, (results, status) => {
      if (String(status) === PLACES_STATUS_OK && results) {
        resolve(results);
      } else {
        resolve([]);
      }
    });
  });
}

function toSuggestion(
  placeId: string,
  description: string,
  mainText?: string,
  secondaryText?: string,
): AddressSuggestion {
  return { placeId, description, mainText, secondaryText };
}

function fromPrediction(p: google.maps.places.AutocompletePrediction): AddressSuggestion {
  return toSuggestion(
    p.place_id,
    p.description,
    p.structured_formatting?.main_text,
    p.structured_formatting?.secondary_text,
  );
}

function fromTextResult(place: google.maps.places.PlaceResult): AddressSuggestion | null {
  if (!place.place_id) return null;
  const description = place.formatted_address ?? place.name ?? "";
  if (!description) return null;
  return toSuggestion(place.place_id, description, place.name);
}

function mergeSuggestions(lists: AddressSuggestion[][]): AddressSuggestion[] {
  const seen = new Set<string>();
  const merged: AddressSuggestion[] = [];
  for (const list of lists) {
    for (const item of list) {
      if (seen.has(item.placeId)) continue;
      seen.add(item.placeId);
      merged.push(item);
    }
  }
  return merged;
}

/** Prefer street-level addresses; drop bare postcode-only rows when we have finer matches. */
function preferAddresses(
  suggestions: AddressSuggestion[],
  postcode: string | null,
): AddressSuggestion[] {
  if (!postcode || suggestions.length === 0) return suggestions;

  const compact = postcode.replace(/\s/g, "").toUpperCase();
  const withStreet = suggestions.filter((s) => {
    const main = (s.mainText ?? s.description).toUpperCase();
    const isPostcodeOnly =
      main.replace(/\s/g, "") === compact ||
      /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(main.trim());
    return !isPostcodeOnly;
  });

  return withStreet.length > 0 ? withStreet : suggestions;
}

/**
 * Fetch UK address suggestions via Google Places, biased to a postcode area when present.
 */
export async function fetchAddressSuggestions(
  input: string,
  autocompleteService: google.maps.places.AutocompleteService,
  geocoder: google.maps.Geocoder,
  placesService: google.maps.places.PlacesService,
): Promise<AddressSuggestion[]> {
  const trimmed = input.trim();
  if (!trimmed) return [];

  const postcode = extractUkPostcode(trimmed);
  const postcodeOnly = isUkPostcodeOnly(trimmed);
  const boundsSource = postcode ? await geocodePostcode(geocoder, postcode) : null;
  const bounds = boundsSource?.geometry.bounds ?? boundsSource?.geometry.viewport;

  const baseRequest: google.maps.places.AutocompletionRequest = {
    input: trimmed,
    componentRestrictions: { country: "gb" },
    types: ["address"],
    ...(bounds ? { bounds } : {}),
  };

  const lists: AddressSuggestion[][] = [];

  lists.push(
    (await autocompletePredictions(autocompleteService, baseRequest)).map(fromPrediction),
  );

  // Airports, terminals, hotels, and stations are not returned with types: ['address'] alone.
  lists.push(
    (
      await autocompletePredictions(autocompleteService, {
        input: trimmed,
        componentRestrictions: { country: "gb" },
        ...(bounds ? { bounds } : {}),
      })
    ).map(fromPrediction),
  );

  if (postcode) {
    lists.push(
      (
        await autocompletePredictions(autocompleteService, {
          input: `${postcode}, `,
          componentRestrictions: { country: "gb" },
          types: ["address"],
          ...(bounds ? { bounds } : {}),
        })
      ).map(fromPrediction),
    );
  }

  if (postcode && postcodeOnly && bounds) {
    const textResults = await textSearchAddresses(
      placesService,
      `${postcode}, UK`,
      bounds,
    );
    const fromText = textResults
      .map((p) => fromTextResult(p))
      .filter((s): s is AddressSuggestion => s != null);
    lists.push(fromText);
  }

  let merged = preferAddresses(mergeSuggestions(lists), postcode);

  if (merged.length === 0 && !postcode) {
    const textResults = await textSearchAddresses(placesService, `${trimmed}, UK`);
    merged = textResults
      .map((p) => fromTextResult(p))
      .filter((s): s is AddressSuggestion => s != null);
  }

  return merged.slice(0, 12);
}

export type AddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

export type PlacesLocation = { lat: number; lng: number };

/** Extract UK postcode from Google address components or the formatted address. */
export function postalCodeFromPlace(
  address: string,
  components: AddressComponent[] | google.maps.GeocoderAddressComponent[],
): string {
  const postcodeComponent = components.find((c) => c.types.includes("postal_code"));
  if (postcodeComponent) {
    return postcodeComponent.long_name || postcodeComponent.short_name || "";
  }
  return extractUkPostcode(address) ?? "";
}
